import { PropertyCode } from 'src/modules/shared/components/atoms/property-code';
import {
  PiArrowSquareOut,
  PiChatCircleText,
  PiCheckCircle,
  PiFunnel,
  PiHouse,
  PiLightning,
  PiListChecks,
  PiPhone,
  PiTarget,
  PiUser,
  PiWarningCircle,
} from 'react-icons/pi';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { Badge } from 'src/modules/shared/components/atoms/badge';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { CardBody } from 'src/modules/shared/components/atoms/cards/card-body';
import { CardHeader } from 'src/modules/shared/components/atoms/cards/card-header';
import { IconBadge } from 'src/modules/shared/components/atoms/icon-badge';
import { Text } from 'src/modules/shared/components/atoms/text';
import { ExpandableSection } from 'src/modules/shared/components/molecules/expandable-section';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { getToolMetadata } from 'src/modules/automations/utils/employees/employee-tool-presentation.util';
import type {
  ExecutionActionSummary,
  ExecutionBlockerSummary,
  ExecutionContactAddressSummary,
  ExecutionContextSummary,
  ExecutionDetailPresentation,
  ExecutionMessageSummary,
  ExecutionPlanSummary,
  ExecutionPropertySummary,
  ExecutionTriggerSummary,
} from 'src/modules/automations/types/executions/execution-detail-presentation.type';
import { ExecutionOutcomePill } from 'src/modules/automations/components/molecules/employees/executions/execution-outcome-pill';
import { getTriggerMetadata } from 'src/modules/automations/utils/executions/timeline/trigger-metadata.util';

interface ExecutionSummaryProps {
  /** Business-facing execution projection. */
  presentation: ExecutionDetailPresentation;
}

interface SummaryCardProps {
  /** Translated card title. */
  title: string;
  /** Card icon. */
  icon: React.ReactNode;
  /** Card content. */
  children: React.ReactNode;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, icon, children }) => (
  <Card variant="secondary" appearance="outline" shadow="none">
    <CardHeader className="flex items-center gap-3 border-slate-100">
      <IconBadge icon={icon} size="sm" variant="primary" appearance="soft" />
      <Text size="paragraph" weight="semibold">
        {title}
      </Text>
    </CardHeader>
    <CardBody>{children}</CardBody>
  </Card>
);

const ContextValue: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
}> = ({ icon, label, value }) => (
  <div className="flex min-w-0 items-start gap-3 rounded-lg bg-slate-50 px-3 py-2.5">
    <span className="mt-0.5 shrink-0 text-slate-500">{icon}</span>
    <div className="min-w-0">
      <Text size="tiny" className="text-slate-500">
        {label}
      </Text>
      <Text size="small" weight="medium" className="break-words text-slate-800">
        {value}
      </Text>
    </div>
  </div>
);

const ExecutionBlockerAlert: React.FC<{
  blocker: ExecutionBlockerSummary;
}> = ({ blocker }) => {
  const { t } = useI18n();

  return (
    <Alert
      variant="warning"
      appearance="soft"
      size="md"
      icon={<PiWarningCircle className="h-5 w-5" />}
      message={
        <div className="space-y-1">
          <Text size="small" weight="semibold">
            {t('automations.aiAgents.detail.blocker')}
          </Text>
          <Text size="small">{blocker.reason}</Text>
          {blocker.affectedWork !== undefined ? (
            <Text size="tiny" className="text-warning-800">
              {t('automations.aiAgents.detail.affectedWork')}:{' '}
              {blocker.affectedWork}
            </Text>
          ) : null}
        </div>
      }
    />
  );
};

const ExecutionContextCard: React.FC<{ context: ExecutionContextSummary }> = ({
  context,
}) => {
  const { t } = useI18n();
  const hasDetailedContext = [
    context.trigger,
    context.contactAddresses.length > 0,
    context.funnelStatusDescription,
    context.recentMessages.length > 0,
    context.callsCount > 0,
    context.propertyDetails,
  ].some(Boolean);
  const hasContext = [
    context.contact,
    context.funnel,
    context.funnelStatus,
    context.property,
    context.latestInteraction,
    hasDetailedContext,
  ].some(Boolean);

  return (
    <SummaryCard
      title={t('automations.aiAgents.detail.context')}
      icon={<PiChatCircleText className="h-4 w-4" />}
    >
      {hasContext ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {context.contact !== undefined ? (
            <ContextValue
              icon={<PiUser className="h-4 w-4" />}
              label={t('automations.aiAgents.detail.contact')}
              value={context.contact}
            />
          ) : null}
          {context.funnel !== undefined ? (
            <ContextValue
              icon={<PiFunnel className="h-4 w-4" />}
              label={t('automations.aiAgents.detail.funnel')}
              value={context.funnel}
            />
          ) : null}
          {context.funnelStatus !== undefined ? (
            <ContextValue
              icon={<PiCheckCircle className="h-4 w-4" />}
              label={t('automations.aiAgents.detail.funnelStatus')}
              value={context.funnelStatus}
            />
          ) : null}
          {context.property !== undefined ? (
            <ContextValue
              icon={<PiHouse className="h-4 w-4" />}
              label={t('automations.aiAgents.detail.property')}
              value={context.property}
            />
          ) : null}
          {context.latestInteraction !== undefined ? (
            <div className="sm:col-span-2">
              <ContextValue
                icon={<PiChatCircleText className="h-4 w-4" />}
                label={t('automations.aiAgents.detail.latestInteraction')}
                value={context.latestInteraction}
              />
            </div>
          ) : null}
          {hasDetailedContext ? (
            <div className="sm:col-span-2">
              <ExpandableSection
                title={t('automations.aiAgents.detail.moreContext')}
                isInitiallyExpanded={false}
                isContentScrollable
                contentClassName="max-h-[28rem]"
                className="rounded-lg border border-slate-100 bg-white"
                compactHeaderOnMobile
              >
                <ExecutionContextDetails context={context} />
              </ExpandableSection>
            </div>
          ) : null}
        </div>
      ) : (
        <Text size="small" className="text-slate-500">
          {t('automations.aiAgents.detail.noContext')}
        </Text>
      )}
    </SummaryCard>
  );
};

const DetailSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ title, icon, children, className = '' }) => (
  <div className={`rounded-lg bg-slate-50 p-3 ${className}`}>
    <div className="mb-2 flex items-center gap-2 text-slate-600">
      {icon}
      <Text size="tiny" weight="semibold">
        {title}
      </Text>
    </div>
    {children}
  </div>
);

const ExecutionContextDetails: React.FC<{
  context: ExecutionContextSummary;
}> = ({ context }) => (
  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
    <TriggerContext trigger={context.trigger} />
    <ContactChannels addresses={context.contactAddresses} />
    <StatusInstructions description={context.funnelStatusDescription} />
    <RecentInteractions
      messages={context.recentMessages}
      totalMessages={context.totalMessages}
      callsCount={context.callsCount}
    />
    <PropertyContext
      property={context.propertyDetails}
      fallbackLabel={context.property}
    />
  </div>
);

const TriggerContext: React.FC<{
  trigger?: ExecutionTriggerSummary;
}> = ({ trigger }) => {
  const { t } = useI18n();
  const triggerMetadata = trigger
    ? getTriggerMetadata(trigger.triggerId ?? '', trigger.resourceAction, t)
    : undefined;

  if (!trigger) return null;

  return (
    <DetailSection
      title={t('automations.aiAgents.detail.trigger')}
      icon={<PiLightning className="h-4 w-4" />}
    >
      <Text size="small" weight="medium" className="text-slate-800">
        {triggerMetadata?.displayName ?? trigger.triggerId}
      </Text>
      {[trigger.resourceAction, trigger.resourceType]
        .filter((value): value is string => Boolean(value))
        .map((value) => (
          <Badge
            key={value}
            variant="neutral"
            appearance="soft"
            size="xs"
            className="mr-1 mt-2"
          >
            {value}
          </Badge>
        ))}
      {trigger.objective ? (
        <Text size="small" className="mt-2 text-slate-700">
          {trigger.objective}
        </Text>
      ) : null}
      {trigger.content ? (
        <Text size="small" className="mt-2 text-slate-600">
          {trigger.sender ? `${trigger.sender}: ` : ''}
          {trigger.content}
        </Text>
      ) : null}
    </DetailSection>
  );
};

const ContactChannels: React.FC<{
  addresses: ExecutionContactAddressSummary[];
}> = ({ addresses }) => {
  const { t } = useI18n();
  if (addresses.length === 0) return null;

  return (
    <DetailSection
      title={t('automations.aiAgents.detail.contactChannels')}
      icon={<PiUser className="h-4 w-4" />}
    >
      <div className="space-y-2">
        {addresses.map((address, index) => (
          <div
            key={`${address.channelType ?? 'address'}-${index}`}
            className="flex min-w-0 items-center gap-2"
          >
            {address.channelType ? (
              <Badge variant="neutral" appearance="soft" size="xs">
                {address.channelType}
              </Badge>
            ) : null}
            <Text size="small" className="break-all text-slate-700">
              {address.address}
            </Text>
          </div>
        ))}
      </div>
    </DetailSection>
  );
};

const StatusInstructions: React.FC<{ description?: string }> = ({
  description,
}) => {
  const { t } = useI18n();
  if (!description) return null;

  return (
    <DetailSection
      title={t('automations.aiAgents.detail.statusInstructions')}
      icon={<PiFunnel className="h-4 w-4" />}
      className="md:col-span-2"
    >
      <div className="max-h-64 overflow-y-auto pr-2">
        <Text size="small" className="whitespace-pre-wrap text-slate-700">
          {description}
        </Text>
      </div>
    </DetailSection>
  );
};

const RecentInteractions: React.FC<{
  messages: ExecutionMessageSummary[];
  totalMessages: number;
  callsCount: number;
}> = ({ messages, totalMessages, callsCount }) => {
  const { t } = useI18n();
  if (messages.length === 0 && callsCount === 0) return null;

  return (
    <DetailSection
      title={t('automations.aiAgents.detail.recentInteractions')}
      icon={<PiChatCircleText className="h-4 w-4" />}
      className="md:col-span-2"
    >
      <div className="space-y-2">
        {messages.map((message, index) => (
          <div
            key={`${message.direction ?? 'message'}-${index}`}
            className="rounded-md border border-slate-100 bg-white p-2"
          >
            <Badge
              variant={message.direction === 'inbound' ? 'neutral' : 'success'}
              appearance="soft"
              size="xs"
              className="mb-1"
            >
              {message.direction === 'inbound'
                ? t('automations.aiAgents.detail.inbound')
                : t('automations.aiAgents.detail.outbound')}
            </Badge>
            <Text size="small" className="whitespace-pre-wrap text-slate-700">
              {message.content}
            </Text>
          </div>
        ))}
        <div className="flex flex-wrap gap-3 text-slate-600">
          <Text size="tiny">
            {t('automations.aiAgents.detail.messagesShown', {
              shown: messages.length,
              total: totalMessages,
            })}
          </Text>
          {callsCount > 0 ? (
            <span className="flex items-center gap-1">
              <PiPhone className="h-3.5 w-3.5" />
              <Text size="tiny">
                {t('automations.aiAgents.detail.callsCount', {
                  count: callsCount,
                })}
              </Text>
            </span>
          ) : null}
        </div>
      </div>
    </DetailSection>
  );
};

const PropertyContext: React.FC<{
  property?: ExecutionPropertySummary;
  fallbackLabel?: string;
}> = ({ property, fallbackLabel }) => {
  const { t } = useI18n();
  if (!property) return null;
  const sourceLabel = [property.sourceSystem, property.sourceId]
    .filter((value): value is string => Boolean(value))
    .join(' · ');

  return (
    <DetailSection
      title={t('automations.aiAgents.detail.propertyDetails')}
      icon={<PiHouse className="h-4 w-4" />}
      className="md:col-span-2"
    >
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Text size="small" weight="medium" className="text-slate-800">
            {property.name ?? fallbackLabel}
            {property.id !== undefined ? (
              <>
                {' '}
                ·{' '}
                <PropertyCode
                  displayCode={property.displayCode}
                  fallback={`ID ${property.id}`}
                />
              </>
            ) : null}
          </Text>
          {property.isActive !== undefined ? (
            <Badge
              variant={property.isActive ? 'success' : 'warning'}
              appearance="soft"
              size="xs"
            >
              {property.isActive
                ? t('automations.aiAgents.detail.active')
                : t('automations.aiAgents.detail.inactive')}
            </Badge>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            property.operation,
            property.propertyType,
            property.bedrooms !== undefined
              ? t('automations.aiAgents.detail.bedrooms', {
                  count: property.bedrooms,
                })
              : undefined,
            property.bathrooms !== undefined
              ? t('automations.aiAgents.detail.bathrooms', {
                  count: property.bathrooms,
                })
              : undefined,
          ]
            .filter((value): value is string => Boolean(value))
            .map((value) => (
              <Badge key={value} variant="neutral" appearance="soft" size="xs">
                {value}
              </Badge>
            ))}
        </div>
        {[property.address, property.locality, property.region]
          .filter((value): value is string => Boolean(value))
          .map((value) => (
            <Text key={value} size="tiny" className="text-slate-600">
              {value}
            </Text>
          ))}
        {property.rentPrice ? (
          <Text size="small" className="text-slate-700">
            {t('automations.aiAgents.detail.rentPrice')}: {property.rentPrice}
          </Text>
        ) : null}
        {property.salePrice ? (
          <Text size="small" className="text-slate-700">
            {t('automations.aiAgents.detail.salePrice')}: {property.salePrice}
          </Text>
        ) : null}
        {property.sourceUrl ? (
          <a
            href={property.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline"
          >
            {sourceLabel.length > 0
              ? sourceLabel
              : t('automations.aiAgents.detail.openSource')}
            <PiArrowSquareOut className="h-3.5 w-3.5" />
          </a>
        ) : null}
      </div>
    </DetailSection>
  );
};

const ExecutionPlanCard: React.FC<{ plan: ExecutionPlanSummary }> = ({
  plan,
}) => {
  const { t } = useI18n();

  return (
    <SummaryCard
      title={t('automations.aiAgents.detail.plan')}
      icon={<PiTarget className="h-4 w-4" />}
    >
      {plan.todos.length > 0 ? (
        <ol className="space-y-2">
          {plan.todos.map((todo, index) => (
            <li key={`${index}-${todo}`} className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-700">
                {index + 1}
              </span>
              <Text size="small" className="pt-0.5 text-slate-700">
                {todo}
              </Text>
            </li>
          ))}
        </ol>
      ) : (
        <Text size="small" className="text-slate-500">
          {t('automations.aiAgents.detail.noPlan')}
        </Text>
      )}
    </SummaryCard>
  );
};

const ExecutionActionsCard: React.FC<{ actions: ExecutionActionSummary[] }> = ({
  actions,
}) => {
  const { t } = useI18n();

  return (
    <SummaryCard
      title={t('automations.aiAgents.detail.actions')}
      icon={<PiListChecks className="h-4 w-4" />}
    >
      {actions.length > 0 ? (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
          {actions.map((action) => {
            const metadata = getToolMetadata(action.toolName, t);
            return (
              <div
                key={action.key}
                className="flex min-w-0 items-center gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5"
              >
                <IconBadge
                  icon={metadata.icon}
                  size="xs"
                  variant="secondary"
                  appearance="soft"
                />
                <div className="min-w-0">
                  <Text size="small" weight="medium" className="text-slate-800">
                    {metadata.displayName}
                  </Text>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Text size="small" className="text-slate-500">
          {t('automations.aiAgents.detail.noActions')}
        </Text>
      )}
    </SummaryCard>
  );
};

/** Business-facing execution summary composed from shared app primitives. */
export const ExecutionSummary: React.FC<ExecutionSummaryProps> = ({
  presentation,
}) => {
  const { t } = useI18n();

  return (
    <div className="space-y-4">
      <Card variant="secondary" appearance="outline" shadow="none">
        <CardBody className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <Text size="tiny" className="text-slate-500">
              {t('automations.aiAgents.detail.objective')}
            </Text>
            <Text size="h5" weight="semibold" className="mt-1 text-slate-900">
              {presentation.plan.objective ??
                t('automations.aiAgents.detail.noObjective')}
            </Text>
          </div>
          <ExecutionOutcomePill outcome={presentation.outcome} />
        </CardBody>
      </Card>

      {presentation.blocker !== undefined ? (
        <ExecutionBlockerAlert blocker={presentation.blocker} />
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ExecutionContextCard context={presentation.context} />
        <ExecutionPlanCard plan={presentation.plan} />
      </div>

      <ExecutionActionsCard actions={presentation.actions} />
    </div>
  );
};
